import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronRight, Crown, Info, Trophy } from "lucide-react";
import { Avatar, AvatarFallback } from "@v2/components/ui/avatar";
import { Badge } from "@v2/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { cn } from "@v2/lib/utils";
import { supabaseV2 } from "../../lib/supabase";
import { useWorkspaceData } from "../../lib/workspace-data";
import { EmptyState, SectionLoading } from "../../components/state";
import {
  aggregateScore,
  computeBenchmark,
  periodToDays,
  reliabilityOf,
  type EaPosition,
  type PeriodKey,
  type PositionStats,
} from "./comparison-math";

// Composizione del modulo. v1 lavora sui soli 4 macro-ruoli EA (GK / DEF /
// MID / FWD). Significa che un 4-3-3 e un 4-4-2 con CDM + CAM hanno la
// stessa quota MID, e non si distingue terzino da centrale. Estensione
// granulare (CB, LB, ST, ecc.) richiede di esporre `archetypeid` dal JSON
// EA in una RPC futura.
type FormationRow = { position: EaPosition; count: number };
type Formation = { id: string; label: string; rows: FormationRow[] };

const FORMATIONS: Formation[] = [
  { id: "433",  label: "4-3-3",   rows: [
    { position: "forward",    count: 3 },
    { position: "midfielder", count: 3 },
    { position: "defender",   count: 4 },
    { position: "goalkeeper", count: 1 },
  ]},
  { id: "442",  label: "4-4-2",   rows: [
    { position: "forward",    count: 2 },
    { position: "midfielder", count: 4 },
    { position: "defender",   count: 4 },
    { position: "goalkeeper", count: 1 },
  ]},
  { id: "4231", label: "4-2-3-1", rows: [
    { position: "forward",    count: 1 },
    { position: "midfielder", count: 5 },
    { position: "defender",   count: 4 },
    { position: "goalkeeper", count: 1 },
  ]},
  { id: "352",  label: "3-5-2",   rows: [
    { position: "forward",    count: 2 },
    { position: "midfielder", count: 5 },
    { position: "defender",   count: 3 },
    { position: "goalkeeper", count: 1 },
  ]},
  { id: "343",  label: "3-4-3",   rows: [
    { position: "forward",    count: 3 },
    { position: "midfielder", count: 4 },
    { position: "defender",   count: 3 },
    { position: "goalkeeper", count: 1 },
  ]},
  { id: "4222", label: "4-2-2-2", rows: [
    { position: "forward",    count: 2 },
    { position: "midfielder", count: 4 },
    { position: "defender",   count: 4 },
    { position: "goalkeeper", count: 1 },
  ]},
];

type Candidate = { stat: PositionStats; score: number };
type AssignedRow = { row: FormationRow; picks: (Candidate | null)[] };

// Soglia minima di partite nel ruolo per essere eleggibili al Top 11.
// 10 = stessa finestra del "top 10 rating" raffinato: chi ha meno di 10
// match in quel ruolo non ha un campione completo per il rating raffinato
// e non viene messo in formazione né tra le riserve "appena fuori".
const MIN_MATCHES_FOR_TOP11 = 10;

export function Top11View({
  teamId,
  onOpenPlayer,
}: {
  teamId: string;
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useTranslation("players");
  const { players, isLoading } = useWorkspaceData(teamId);

  const [formationId, setFormationId] = useState<string>("433");
  const [period, setPeriod] = useState<PeriodKey>("all");

  const formation = useMemo(
    () => FORMATIONS.find((f) => f.id === formationId) ?? FORMATIONS[0],
    [formationId],
  );

  // I player con almeno uno tra ea_account_handle e ea_sports_id sono gli
  // unici "valutabili": la RPC join via questi due campi.
  const eligibleIds = useMemo(
    () => players.filter((p) => p.ea_account_handle || p.ea_sports_id).map((p) => p.id),
    [players],
  );

  // Le macro effettivamente presenti nel modulo (in pratica tutte e 4 nei
  // moduli predefiniti, ma manteniamo flessibile per moduli futuri).
  const macroPositions = useMemo(() => {
    const seen = new Set<EaPosition>();
    formation.rows.forEach((r) => seen.add(r.position));
    return Array.from(seen);
  }, [formation]);

  // Una query per macro, parallele. La RPC del confronto è la stessa: con
  // tutti i player eligible e p_position fissa ritorna chi ha giocato in
  // quel ruolo e quanto. Niente RPC nuova.
  const positionQueries = useQueries({
    queries: macroPositions.map((pos) => ({
      queryKey: ["top11-stats", teamId, pos, period, eligibleIds.join(",")],
      enabled: eligibleIds.length > 0,
      queryFn: async () => {
        const { data, error } = await supabaseV2.rpc("team_player_position_stats", {
          p_team_id: teamId,
          p_player_ids: eligibleIds,
          p_position: pos,
          p_period_days: periodToDays(period),
        });
        if (error) throw error;
        return data as { players: PositionStats[] };
      },
      staleTime: 30_000,
    })),
  });

  const isAnyLoading = positionQueries.some((q) => q.isLoading);

  // Per ogni macro, lista candidati ordinata per score desc. Il benchmark si
  // calcola sui player attivi in quella posizione (incluso solo chi ha >0
  // match nel filtro): è il riferimento "media del ruolo" che usiamo per il
  // refinement (top-10 rating + shrinkage).
  const candidatesByPosition = useMemo(() => {
    const map = new Map<EaPosition, Candidate[]>();
    macroPositions.forEach((pos, idx) => {
      // Benchmark calcolato su tutti i player con almeno 1 match nel ruolo,
      // così la "media squadra" resta rappresentativa anche se la formazione
      // poi esclude chi ha solo 1 match.
      const rowsWithAny = (positionQueries[idx].data?.players || []).filter((s) => s.matches > 0);
      const benchmark = computeBenchmark(rowsWithAny);
      // I candidati per gli slot del modulo invece richiedono la soglia minima
      // di MIN_MATCHES_FOR_TOP11 partite per evitare titolari "casuali".
      const enriched: Candidate[] = rowsWithAny
        .filter((s) => s.matches >= MIN_MATCHES_FOR_TOP11)
        .map((stat) => ({ stat, score: aggregateScore(stat, pos, benchmark) }))
        .sort((a, b) => b.score - a.score);
      map.set(pos, enriched);
    });
    return map;
  }, [macroPositions, positionQueries]);

  // Assegnazione greedy: per ogni riga prendiamo i top N tra i candidati non
  // ancora usati. Un player può comparire una sola volta in formazione
  // (anche se ha giocato in 2 macro diverse: il suo posto migliore - score
  // più alto - viene assegnato per primo perché le righe sono iterate dal
  // reparto offensivo a scendere e i candidati sono già ordinati per score).
  const assignment: AssignedRow[] = useMemo(() => {
    const used = new Set<string>();
    return formation.rows.map((row) => {
      const candidates = candidatesByPosition.get(row.position) || [];
      const picks: (Candidate | null)[] = [];
      for (const c of candidates) {
        if (picks.length >= row.count) break;
        if (used.has(c.stat.player_id)) continue;
        used.add(c.stat.player_id);
        picks.push(c);
      }
      while (picks.length < row.count) picks.push(null);
      return { row, picks };
    });
  }, [formation, candidatesByPosition]);

  // Riserve: per ogni macro, i candidati esclusi dalla formazione titolare.
  // Utile al coach per vedere chi è "fuori per un soffio".
  const benches = useMemo(() => {
    const titularIds = new Set(
      assignment.flatMap((r) => r.picks.filter((p): p is Candidate => !!p).map((p) => p.stat.player_id)),
    );
    return macroPositions
      .map((pos) => ({
        position: pos,
        list: (candidatesByPosition.get(pos) || []).filter((c) => !titularIds.has(c.stat.player_id)).slice(0, 3),
      }))
      .filter((b) => b.list.length > 0);
  }, [assignment, candidatesByPosition, macroPositions]);

  if (isLoading) return <SectionLoading />;

  return (
    <div className="space-y-4 animate-fade-in">
      <section>
        <h2 className="text-2xl font-semibold">{t("top11.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("top11.subtitle")}</p>
      </section>

      {/* Selettori modulo + periodo */}
      <Card className="shadow-card">
        <CardContent className="p-4 space-y-3">
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">{t("top11.formation")}</p>
            <div className="flex flex-wrap gap-1.5">
              {FORMATIONS.map((f) => (
                <PillButton
                  key={f.id}
                  active={f.id === formationId}
                  onClick={() => setFormationId(f.id)}
                >
                  {f.label}
                </PillButton>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              {t("compare.config.period")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(["30", "90", "season", "all"] as PeriodKey[]).map((p) => (
                <PillButton key={p} active={p === period} onClick={() => setPeriod(p)}>
                  {t(`compare.period.${p}`)}
                </PillButton>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-400">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span className="leading-snug">{t("top11.macroNote")}</span>
          </div>
          <div className="flex items-start gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-[11px] text-primary">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span className="leading-snug">
              {t("top11.minMatchesNote", { min: MIN_MATCHES_FOR_TOP11 })}
            </span>
          </div>
        </CardContent>
      </Card>

      {eligibleIds.length === 0 && (
        <Card className="shadow-card">
          <CardContent className="py-8">
            <EmptyState text={t("top11.noEligible")} />
          </CardContent>
        </Card>
      )}

      {eligibleIds.length > 0 && isAnyLoading && <SectionLoading />}

      {eligibleIds.length > 0 && !isAnyLoading && (
        <>
          <Card className="shadow-card overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="h-4 w-4 text-primary" />
                {t("top11.lineupTitle", { formation: formation.label })}
              </CardTitle>
              <CardDescription>
                {t("top11.lineupDesc", { period: t(`compare.period.${period}`) })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Pitch assignment={assignment} t={t} onOpenPlayer={onOpenPlayer} />
            </CardContent>
          </Card>

          {/* Roster dettagliato sotto al campo: comodo per scorrere tutti gli
              undici titolari con score e numero match. */}
          <Card className="shadow-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t("top11.rosterTitle")}</CardTitle>
              <CardDescription>{t("top11.rosterDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {assignment.map((r, idx) => (
                <RosterGroup
                  key={idx}
                  row={r}
                  t={t}
                  onOpenPlayer={onOpenPlayer}
                />
              ))}
            </CardContent>
          </Card>

          {/* Riserve: i 3 candidati più vicini a entrare in formazione per
              ogni reparto. Aiuta il coach a vedere chi è "appena fuori". */}
          {benches.length > 0 && (
            <Card className="shadow-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t("top11.benchTitle")}</CardTitle>
                <CardDescription>{t("top11.benchDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {benches.map((b) => (
                  <div key={b.position}>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                      {t(`top11.row.${b.position}`)}
                    </p>
                    <div className="space-y-1">
                      {b.list.map((c) => (
                        <RosterRow
                          key={c.stat.player_id}
                          candidate={c}
                          t={t}
                          onOpenPlayer={onOpenPlayer}
                          muted
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Pitch: rappresentazione visiva della formazione. Niente SVG complesso,
// solo righe flex con il "tile" del player. Vivibile sia su mobile (393px)
// sia su desktop ampi.
// ──────────────────────────────────────────────────────────────────────────

function Pitch({
  assignment,
  t,
  onOpenPlayer,
}: {
  assignment: AssignedRow[];
  t: (k: string, opts?: any) => string;
  onOpenPlayer: (id: string) => void;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl bg-raised",
        "p-3 sm:p-5 flex flex-col justify-between gap-3 sm:gap-5",
        "min-h-[420px] sm:min-h-[520px]",
        "relative overflow-hidden",
      )}
    >
      {/* Linee campo: cerchio di centrocampo + linea metà campo. Sfondo
          decorativo, niente interazione. */}
      <div aria-hidden className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-1/3 aspect-square rounded-full border border-white/10" />
      </div>
      <div aria-hidden className="absolute inset-0 pointer-events-none flex items-center">
        <div className="w-full h-px bg-white/10" />
      </div>
      {assignment.map((row, rowIdx) => (
        <div key={rowIdx} className="relative flex justify-center items-center gap-2 sm:gap-3 flex-wrap">
          {row.picks.map((pick, j) => (
            <PitchTile
              key={j}
              pick={pick}
              position={row.row.position}
              t={t}
              onOpenPlayer={onOpenPlayer}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function PitchTile({
  pick,
  position,
  t,
  onOpenPlayer,
}: {
  pick: Candidate | null;
  position: EaPosition;
  t: (k: string, opts?: any) => string;
  onOpenPlayer: (id: string) => void;
}) {
  if (!pick) {
    return (
      <div className="w-[68px] sm:w-[90px] flex flex-col items-center gap-1 text-center opacity-60">
        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full border-[1.5px] border-dashed border-[hsl(var(--input))]" />
        <p className="text-[9px] sm:text-[10px] text-white/60 uppercase tracking-wider font-semibold">
          {t(`top11.row.${position}`)}
        </p>
        <p className="text-[9px] sm:text-[10px] text-white/40 italic leading-tight">
          {t("top11.noCandidate")}
        </p>
      </div>
    );
  }
  const initials = pick.stat.display_name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <button
      type="button"
      onClick={() => onOpenPlayer(pick.stat.player_id)}
      className="w-[68px] sm:w-[90px] flex flex-col items-center gap-1 text-center group"
    >
      <div className="relative">
        <Avatar className="h-10 w-10 sm:h-12 sm:w-12 group-hover:ring-2 group-hover:ring-foreground/70 transition-shadow">
          <AvatarFallback className="text-[10px] sm:text-xs font-bold bg-card text-foreground">
            {initials}
          </AvatarFallback>
        </Avatar>
        <span className="absolute -top-1.5 -right-1.5 rounded-full bg-primary text-primary-foreground text-[9px] sm:text-[10px] font-bold tabular-nums px-1.5 py-0.5 shadow">
          {pick.score}
        </span>
      </div>
      <p className="text-[10px] sm:text-[11px] font-semibold text-white leading-tight max-w-full truncate">
        {pick.stat.display_name}
      </p>
      <p className="text-[8px] sm:text-[9px] text-white/70 uppercase tracking-wider leading-tight">
        {t(`top11.row.${position}`)}
      </p>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Lista titolari raggruppata per reparto, con score, match e badge di
// affidabilità. Click sul nome apre la scheda del giocatore.
// ──────────────────────────────────────────────────────────────────────────

function RosterGroup({
  row,
  t,
  onOpenPlayer,
}: {
  row: AssignedRow;
  t: (k: string, opts?: any) => string;
  onOpenPlayer: (id: string) => void;
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
        {t(`top11.row.${row.row.position}`)}
      </p>
      <div className="space-y-1">
        {row.picks.map((pick, j) =>
          pick ? (
            <RosterRow key={pick.stat.player_id} candidate={pick} t={t} onOpenPlayer={onOpenPlayer} />
          ) : (
            <div key={j} className="rounded-lg border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground italic">
              {t("top11.noCandidate")}
            </div>
          ),
        )}
      </div>
    </div>
  );
}

function RosterRow({
  candidate,
  t,
  onOpenPlayer,
  muted = false,
}: {
  candidate: Candidate;
  t: (k: string, opts?: any) => string;
  onOpenPlayer: (id: string) => void;
  muted?: boolean;
}) {
  const reliability = reliabilityOf(candidate.stat.matches);
  const reliabilityTone =
    reliability === "high"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
      : reliability === "medium"
        ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
        : "bg-rose-500/15 text-rose-700 dark:text-rose-400";
  return (
    <button
      type="button"
      onClick={() => onOpenPlayer(candidate.stat.player_id)}
      className={cn(
        "w-full flex items-center gap-2 rounded-lg border bg-card/60 px-3 py-2 text-left hover:bg-muted/40 transition-colors",
        muted && "opacity-90",
      )}
    >
      <Avatar className="h-8 w-8">
        <AvatarFallback className="text-[10px] font-semibold">
          {candidate.stat.display_name.split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate flex items-center gap-1.5">
          {!muted && <Crown className="h-3 w-3 text-primary shrink-0" />}
          <span className="truncate">{candidate.stat.display_name}</span>
          {candidate.stat.is_guest && (
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground shrink-0">
              {t("compare.guestTag")}
            </span>
          )}
        </p>
        <p className="text-[11px] text-muted-foreground tabular-nums truncate">
          {candidate.stat.matches} {t("compare.matchesUnit")}
        </p>
      </div>
      <Badge
        variant="outline"
        className={cn(
          "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border-0",
          reliabilityTone,
        )}
        title={t(`compare.sample.${reliability}Tip`)}
      >
        {t(`compare.sample.${reliability}`)}
      </Badge>
      <div className="text-right shrink-0 w-12">
        <p className="text-base font-bold tabular-nums text-primary leading-tight">
          {candidate.score}
        </p>
        <p className="text-[9px] text-muted-foreground leading-tight">/100</p>
      </div>
      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    </button>
  );
}

// Riusato dal confronto: piccolo bottone pill per i filtri.
function PillButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-smooth",
        active
          ? "border-primary bg-primary/10 text-primary"
          : "bg-card hover:bg-muted/60 text-foreground/80",
      )}
    >
      {children}
    </button>
  );
}
